import React from "react";
import Helmet from "react-helmet";
import { Link } from "react-router-dom";
import { Navbar2, Footer2 } from "../../components";
import SeriesNav from "../../components/labs/SeriesNav";
import Zoomable from "../../components/Zoomable/Zoomable";
import { getRouteMeta } from "../../seoData";
import adServices from "../../images/labs/ad-services.png";
import adComputers from "../../images/labs/ad-computers.png";
import adUsers from "../../images/labs/ad-users.png";

function DigitalForensicsHomelabActiveDirectory() {
  const { title, description: desc, image, url } = getRouteMeta(
    "/digital-forensics-homelab-active-directory"
  );

  return (
    <div>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} data-react-helmet="true"></meta>

        <meta property="og:type" content="website" data-react-helmet="true"></meta>
        <meta property="og:url" content={url} data-react-helmet="true"></meta>
        <meta property="og:title" content={title} data-react-helmet="true"></meta>
        <meta property="og:description" content={desc} data-react-helmet="true"></meta>
        <meta property="og:image" content={image} data-react-helmet="true"></meta>

        <meta property="twitter:card" content="summary_large_image"></meta>
        <meta property="twitter:url" content={url} data-react-helmet="true"></meta>
        <meta property="twitter:title" content={title} data-react-helmet="true"></meta>
        <meta property="twitter:description" content={desc} data-react-helmet="true"></meta>
        <meta property="twitter:image" content={image} data-react-helmet="true"></meta>
      </Helmet>
      <Navbar2 />
      <section id="blogs">
        <div className="blogs main">
          <div className="blogs-header pb-4">
            <Link to="/projects/" className="blog-back-link">
              <i className="fa-solid fa-arrow-left"></i>&nbsp;Back to Projects
            </Link>
            <h2 className="section-title">Digital Forensics HomeLab</h2>
            <div className="blog-meta">
              <span>
                <i className="fa-solid fa-sitemap"></i>Part 3: Active
                Directory Basics
              </span>
            </div>
          </div>

          <SeriesNav current="/digital-forensics-homelab-active-directory/" />

          <p className="m-5">
            Everything in this lab sits on top of Active Directory, so
            before I get into SYSVOL and Group Policy in the next two
            parts, I want to lay out what AD actually is and how I've come
            to understand it by building it myself on Windows Server 2022.
            I'm not writing this as someone who's mastered AD. I'm still
            learning a lot of it as I go, this is what I've picked up so
            far and where I've had to slow down and look things up.
          </p>

          <h3>What Is Active Directory</h3>
          <p className="m-5">
            Active Directory is Microsoft's directory service: a
            centralized database that stores and organizes everything on a
            Windows network that needs an identity, users, computers,
            groups, and the permissions tying them together. My domain
            controller, <span className="fw-bolder">ZINGDC1</span>, holds
            that database for the <span className="fw-bolder">
              zingnet.local
            </span>{" "}
            domain. Any device that joins the domain, and any user that
            logs into one, gets authenticated and authorized against what's
            stored on that DC.
          </p>

          <h3>The Services That Actually Make It Work</h3>
          <p className="m-5">
            AD isn't one thing, it's a set of services running together on
            the DC. Looking at Server Manager on ZINGDC1, the ones doing
            the real work are:
          </p>
          <div>
            <ul>
              <li className="m-2">
                <span className="fw-bolder">
                  Active Directory Domain Services (NTDS):
                </span>{" "}
                the core engine, the actual directory database.
              </li>
              <li className="m-2">
                <span className="fw-bolder">Netlogon:</span> handles
                authentication, locates domain controllers for login
                requests.
              </li>
              <li className="m-2">
                <span className="fw-bolder">
                  Kerberos Key Distribution Center (Kdc):
                </span>{" "}
                issues the tickets Kerberos authentication runs on.
              </li>
              <li className="m-2">
                <span className="fw-bolder">DNS Server:</span> AD is
                DNS-dependent, clients use DNS to find the DC in the first
                place.
              </li>
              <li className="m-2">
                <span className="fw-bolder">
                  DFS Namespace and DFS Replication (DFSR):
                </span>{" "}
                the service that replicates SYSVOL between domain
                controllers, more on that in Part 4.
              </li>
              <li className="m-2">
                <span className="fw-bolder">
                  Active Directory Web Services (ADWS):
                </span>{" "}
                what the AD PowerShell cmdlets talk to.
              </li>
              <li className="m-2">
                <span className="fw-bolder">Windows Time (W32Time):</span>{" "}
                keeps clocks in sync across the domain, which matters
                because Kerberos will reject requests if the clock skew is
                too large.
              </li>
            </ul>
            <p className="m-5">
              DFS is short for Distributed File System, Microsoft's
              technology for presenting shared folders from multiple
              servers under one logical path and keeping copies of those
              folders in sync. It's really two pieces working together.{" "}
              <span className="fw-bolder">DFS Namespaces</span> is what
              lets a path like{" "}
              <span className="fw-bolder">
                \\zingnet.local\SYSVOL
              </span>{" "}
              resolve to whichever domain controller is actually reachable,
              instead of pointing at one fixed server by name.{" "}
              <span className="fw-bolder">DFS Replication (DFSR)</span> is
              the multi-master engine that keeps the real files in SYSVOL
              identical across every DC, so if I edited a GPO on ZINGDC1,
              DFSR would be what pushed that change out to any other domain
              controllers in the domain. Right now I only have one DC, so
              there's nothing for it to replicate to yet, but it's the same
              mechanism that keeps SYSVOL consistent in a multi-DC domain.
            </p>
            <p className="m-5">
              A couple of services on the list were stopped by design,{" "}
              <span className="fw-bolder">File Replication (NtFrs)</span>{" "}
              is the legacy SYSVOL replication mechanism that DFSR replaced,
              and{" "}
              <span className="fw-bolder">
                Distributed Link Tracking Client
              </span>{" "}
              isn't something I need in a lab this size. Seeing them
              disabled rather than just assuming it was actually a useful
              exercise in confirming what a modern, DFSR-based domain
              should look like.
            </p>
          </div>

          <div className="screenshot-frame">
            <Zoomable label="AD DS services running on ZINGDC1">
              <img
                src={adServices}
                width="840"
                height="585"
                alt="Server Manager showing 13 AD-related services running on ZINGDC1"
              />
            </Zoomable>
          </div>
          <span className="screenshot-caption">
            The full service list on ZINGDC1
          </span>

          <h3>The Lab Environment Itself</h3>
          <p className="m-5">
            Inside Active Directory Users and Computers, I've got a{" "}
            <span className="fw-bolder">LabEnv</span> OU split into a{" "}
            <span className="fw-bolder">Computers</span> container and a{" "}
            <span className="fw-bolder">Regular Users</span> container.
            Computers holds the two domain-joined clients from Part 1 and
            Part 2, registered as{" "}
            <span className="fw-bolder">USER1</span> and{" "}
            <span className="fw-bolder">USER2</span>. Regular Users holds
            five test accounts, User One through User Five, that I use to
            exercise the logon enforcement script I walk through in Part 5.
            Keeping lab objects in their own OU rather than the default
            Users container makes it easier to scope Group Policy to just
            this environment without touching anything else in the domain.
          </p>

          <div className="screenshot-frame">
            <Zoomable label="LabEnv Computers container: USER1 and USER2">
              <img
                src={adComputers}
                width="950"
                height="237"
                alt="Active Directory Users and Computers showing USER1 and USER2 under LabEnv > Computers"
              />
            </Zoomable>
          </div>
          <span className="screenshot-caption">
            LabEnv &gt; Computers: the two domain-joined clients
          </span>

          <div className="screenshot-frame">
            <Zoomable label="LabEnv Regular Users container: five test accounts">
              <img
                src={adUsers}
                width="950"
                height="237"
                alt="Active Directory Users and Computers showing five test user accounts under LabEnv > Regular Users"
              />
            </Zoomable>
          </div>
          <span className="screenshot-caption">
            LabEnv &gt; Regular Users: the five test accounts
          </span>

          <h3>The Role AD Plays in Incidents</h3>
          <p className="m-5">
            From a DFIR angle, AD is the map of who has access to what, so
            it ends up being central evidence in a lot of investigations.
            Authentication logs, Kerberos ticket activity, and Netlogon
            events on the DC are usually where you start when you're trying
            to reconstruct how an account got used, where it logged in
            from, and whether that pattern actually matches what that
            account should be doing. I'm still early in learning how deep
            that goes in a real investigation. Part 4 gets into a piece of
            AD I actually ran into during an incident at work, SYSVOL, and
            why it matters more than its "just a shared folder" reputation
            suggests.
          </p>

          <SeriesNav current="/digital-forensics-homelab-active-directory/" />
        </div>
        <Footer2 />
      </section>
    </div>
  );
}

export default DigitalForensicsHomelabActiveDirectory;
